import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AgentLayout from '../../components/layout/AgentLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import {
  ArrowLeft,
  CheckCircle,
  Clock,
  AlertCircle,
  FileText,
  User,
  Target,
  GraduationCap,
  Plane,
  Home,
  DollarSign,
  Briefcase,
  RefreshCw,
  Loader2,
  Calendar,
  MessageSquare,
  Plus,
  MoreVertical,
  Award,
  AlertTriangle,
  Timer,
  Mail,
  Phone,
  MapPin,
  BookOpen,
  Edit,
  ExternalLink,
  Download,
  Upload,
  History,
  StickyNote,
  Users,
  Flag,
  Zap
} from 'lucide-react';
import { agentAPI, chatAPI, taskAPI, uploadAPI } from '../../services/api';
import { useRoleBasePath } from '../../hooks/useRoleBasePath';
import ServiceChat from '../../components/services/ServiceChat';
import FormDataViewer from '../../components/services/hub/FormDataViewer';
import { toast } from 'sonner';
import { format, formatDistanceToNow, isPast, isToday, isTomorrow } from 'date-fns';
import { cn } from '../../lib/utils';

// Service type configuration
const SERVICE_TYPE_CONFIG = {
  PROFILE_ASSESSMENT: { label: 'Profile Assessment', icon: Target, color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-50', textColor: 'text-blue-700' },
  UNIVERSITY_SHORTLISTING: { label: 'Preapplication Support', icon: GraduationCap, color: 'from-purple-500 to-purple-600', bgColor: 'bg-purple-50', textColor: 'text-purple-700' },
  APPLICATION_ASSISTANCE: { label: 'Apply University', icon: FileText, color: 'from-pink-500 to-pink-600', bgColor: 'bg-pink-50', textColor: 'text-pink-700' },
  VISA_GUIDANCE: { label: 'Visa & Interview Support', icon: Plane, color: 'from-orange-500 to-orange-600', bgColor: 'bg-orange-50', textColor: 'text-orange-700' },
  SCHOLARSHIP_SEARCH: { label: 'Ticket & Travel Support', icon: Award, color: 'from-green-500 to-green-600', bgColor: 'bg-green-50', textColor: 'text-green-700' },
  LOAN_ASSISTANCE: { label: 'Education Loan', icon: DollarSign, color: 'from-emerald-500 to-emerald-600', bgColor: 'bg-emerald-50', textColor: 'text-emerald-700' },
  ACCOMMODATION_HELP: { label: 'Find Accommodation', icon: Home, color: 'from-cyan-500 to-cyan-600', bgColor: 'bg-cyan-50', textColor: 'text-cyan-700' },
  PRE_DEPARTURE_ORIENTATION: { label: 'Find Jobs Abroad', icon: Briefcase, color: 'from-indigo-500 to-indigo-600', bgColor: 'bg-indigo-50', textColor: 'text-indigo-700' }
};

// Status configuration
const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  WAITING_STUDENT: { label: 'Waiting Student', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200' },
  ON_HOLD: { label: 'On Hold', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-200' }
};

// Priority configuration
const PRIORITY_CONFIG = {
  LOW: { label: 'Low', color: 'bg-gray-100 text-gray-600', icon: null },
  MEDIUM: { label: 'Medium', color: 'bg-blue-100 text-blue-600', icon: null },
  HIGH: { label: 'High', color: 'bg-orange-100 text-orange-600', icon: AlertTriangle },
  URGENT: { label: 'Urgent', color: 'bg-red-100 text-red-600', icon: AlertCircle }
};

// Task status configuration
const TASK_STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: 'bg-gray-100 text-gray-700' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  SUBMITTED: { label: 'Submitted', color: 'bg-purple-100 text-purple-700' },
  UNDER_REVIEW: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  REVISION_REQUIRED: { label: 'Revision Required', color: 'bg-orange-100 text-orange-700' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' }
};

// Task type configuration
const TASK_TYPE_CONFIG = {
  DOCUMENT_UPLOAD: { label: 'Document Upload', icon: Upload },
  QUESTIONNAIRE: { label: 'Questionnaire', icon: FileText },
  VIDEO_CALL: { label: 'Video Call', icon: Users },
  REVIEW_SESSION: { label: 'Review Session', icon: BookOpen },
  INFORMATION_SUBMISSION: { label: 'Information Submission', icon: Edit },
  FORM_COMPLETION: { label: 'Form Completion', icon: FileText },
  PAYMENT: { label: 'Payment', icon: DollarSign },
  APPROVAL_REQUIRED: { label: 'Approval Required', icon: CheckCircle },
  OTHER: { label: 'Other', icon: Zap }
};

export default function AgentServiceRequestDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const basePath = useRoleBasePath();

  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal states
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [priorityModalOpen, setPriorityModalOpen] = useState(false);
  const [deadlineModalOpen, setDeadlineModalOpen] = useState(false);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);

  // Form states
  const [newStatus, setNewStatus] = useState('');
  const [newProgress, setNewProgress] = useState(0);
  const [newPriority, setNewPriority] = useState('');
  const [newDeadline, setNewDeadline] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isNoteInternal, setIsNoteInternal] = useState(false);
  const [statusNote, setStatusNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // New task form
  const [newTask, setNewTask] = useState({
    taskType: '',
    title: '',
    description: '',
    instructions: '',
    dueDate: '',
    priority: 'MEDIUM'
  });

  useEffect(() => {
    if (id) {
      fetchCaseDetails();
    }
  }, [id]);

  const fetchCaseDetails = async () => {
    try {
      setLoading(true);
      const response = await agentAPI.getCaseById(id);
      setCaseData(response.case);
      setNewStatus(response.case.status);
      setNewProgress(response.case.progress || 0);
      setNewPriority(response.case.priority || 'MEDIUM');
      setNewDeadline(response.case.deadline ? format(new Date(response.case.deadline), 'yyyy-MM-dd') : '');
    } catch (error) {
      console.error('Failed to fetch case details:', error);
      toast.error('Failed to load case details');
      navigate(`${basePath}/service-requests`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      setSubmitting(true);
      await agentAPI.updateCaseStatus(id, newStatus, statusNote);
      toast.success('Status updated successfully');
      setStatusModalOpen(false);
      setStatusNote('');
      fetchCaseDetails();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateProgress = async () => {
    try {
      setSubmitting(true);
      await agentAPI.updateCaseProgress(id, newProgress);
      toast.success('Progress updated successfully');
      setProgressModalOpen(false);
      fetchCaseDetails();
    } catch (error) {
      console.error('Failed to update progress:', error);
      toast.error('Failed to update progress');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePriority = async () => {
    try {
      setSubmitting(true);
      await agentAPI.updateCasePriority(id, newPriority);
      toast.success('Priority updated successfully');
      setPriorityModalOpen(false);
      fetchCaseDetails();
    } catch (error) {
      console.error('Failed to update priority:', error);
      toast.error('Failed to update priority');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateDeadline = async () => {
    try {
      setSubmitting(true);
      await agentAPI.updateCaseDeadline(id, newDeadline || null);
      toast.success('Deadline updated successfully');
      setDeadlineModalOpen(false);
      fetchCaseDetails();
    } catch (error) {
      console.error('Failed to update deadline:', error);
      toast.error('Failed to update deadline');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    try {
      setSubmitting(true);
      await agentAPI.addCaseNote(id, newNote, isNoteInternal);
      toast.success('Note added successfully');
      setNoteModalOpen(false);
      setNewNote('');
      setIsNoteInternal(false);
      fetchCaseDetails();
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.taskType || !newTask.title || !newTask.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      setSubmitting(true);
      await agentAPI.createCaseTask(id, newTask);
      toast.success('Task created successfully');
      setCreateTaskModalOpen(false);
      setNewTask({
        taskType: '',
        title: '',
        description: '',
        instructions: '',
        dueDate: '',
        priority: 'MEDIUM'
      });
      fetchCaseDetails();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await agentAPI.completeTask(taskId);
      toast.success('Task marked as complete');
      fetchCaseDetails();
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast.error('Failed to complete task');
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading case details...</p>
          </div>
        </div>
      </AgentLayout>
    );
  }

  if (!caseData) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Case Not Found</h2>
            <p className="text-gray-600 mb-4">The requested case could not be found.</p>
            <Button onClick={() => navigate(`${basePath}/service-requests`)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Pipeline
            </Button>
          </div>
        </div>
      </AgentLayout>
    );
  }

  const serviceConfig = SERVICE_TYPE_CONFIG[caseData.serviceType] || {};
  const ServiceIcon = serviceConfig.icon || FileText;
  const statusConfig = STATUS_CONFIG[caseData.status] || {};
  const priorityConfig = PRIORITY_CONFIG[caseData.priority] || PRIORITY_CONFIG.MEDIUM;

  const isOverdue = caseData.deadline && isPast(new Date(caseData.deadline)) && caseData.status !== 'COMPLETED';

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`${basePath}/service-requests`)}
              className="mb-2 -ml-2"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Pipeline
            </Button>

            <div className="flex items-center gap-3">
              <div className={cn("w-12 h-12 rounded-lg flex items-center justify-center bg-gradient-to-br", serviceConfig.color)}>
                <ServiceIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-outfit font-bold text-gray-900">
                  {serviceConfig.label}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                  {priorityConfig.icon && (
                    <Badge className={priorityConfig.color}>
                      <priorityConfig.icon className="w-3 h-3 mr-1" />
                      {priorityConfig.label}
                    </Badge>
                  )}
                  {isOverdue && (
                    <Badge className="bg-red-100 text-red-700">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Overdue
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setStatusModalOpen(true)}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Change Status
            </Button>
            <Button onClick={() => setNoteModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Note
            </Button>
          </div>
        </div>

        {/* Partner Info Banner (for rep-counselor cases) */}
        {caseData.isPartnerManaged && caseData.partner && (
          <Card className="border-purple-200 bg-purple-50/60">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-700" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-purple-900">
                      Partner-Managed Case
                    </p>
                    <Badge className="bg-purple-600 text-white text-[10px]">
                      rep-counselor
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-700">
                    Partner: <span className="font-medium">
                      {caseData.partner.firstName} {caseData.partner.lastName}
                    </span>
                    {caseData.partner.email && (
                      <span className="text-gray-500"> · {caseData.partner.email}</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Tasks created for this case will be delivered to the partner, who acts on behalf of the student.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Student Info Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar className="h-14 w-14 border-2 border-white shadow-md">
                  <AvatarImage src={caseData.student?.avatar} />
                  <AvatarFallback className="bg-emerald-100 text-emerald-700 font-semibold text-lg">
                    {caseData.student?.firstName?.[0]}{caseData.student?.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">
                    {caseData.student?.firstName} {caseData.student?.lastName}
                    {caseData.isPartnerManaged && (
                      <Badge className="ml-2 bg-purple-100 text-purple-700 text-[10px] align-middle">
                        Partner Student
                      </Badge>
                    )}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5" />
                      {caseData.student?.email}
                    </span>
                    {caseData.student?.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" />
                        {caseData.student?.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{caseData.progress || 0}%</p>
                  <p className="text-xs text-gray-500">Progress</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{caseData.taskStats?.completed || 0}/{caseData.taskStats?.total || 0}</p>
                  <p className="text-xs text-gray-500">Tasks Done</p>
                </div>
                <div className="text-center">
                  <p className={cn("text-sm font-medium", isOverdue ? "text-red-600" : "text-gray-600")}>
                    {caseData.deadline ? format(new Date(caseData.deadline), 'MMM d, yyyy') : 'No deadline'}
                  </p>
                  <p className="text-xs text-gray-500">Deadline</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-7 w-full max-w-4xl">
            <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Overview</TabsTrigger>
            <TabsTrigger value="student" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Student</TabsTrigger>
            <TabsTrigger value="documents" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Documents</TabsTrigger>
            <TabsTrigger value="tasks" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Tasks ({caseData.taskStats?.total || 0})</TabsTrigger>
            <TabsTrigger value="chat" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Chat</TabsTrigger>
            <TabsTrigger value="notes" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">Notes</TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white">History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Case Details */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Case Overview</CardTitle>
                  <CardDescription>Manage case details and progress</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Progress */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Progress</Label>
                      <Button variant="ghost" size="sm" onClick={() => setProgressModalOpen(true)}>
                        <Edit className="w-3 h-3 mr-1" />
                        Edit
                      </Button>
                    </div>
                    <Progress value={caseData.progress || 0} className="h-3" />
                    <p className="text-sm text-gray-500 mt-1">{caseData.progress || 0}% complete</p>
                  </div>

                  <Separator />

                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Status</Label>
                      <div className="mt-1">
                        <Badge className={cn("text-sm", statusConfig.color)}>{statusConfig.label}</Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setStatusModalOpen(true)}>
                      <Edit className="w-3 h-3 mr-1" />
                      Change
                    </Button>
                  </div>

                  <Separator />

                  {/* Priority */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Priority</Label>
                      <div className="mt-1">
                        <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setPriorityModalOpen(true)}>
                      <Edit className="w-3 h-3 mr-1" />
                      Change
                    </Button>
                  </div>

                  <Separator />

                  {/* Deadline */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Deadline</Label>
                      <p className={cn("mt-1 text-sm", isOverdue ? "text-red-600 font-medium" : "text-gray-600")}>
                        {caseData.deadline ? (
                          <>
                            {format(new Date(caseData.deadline), 'MMMM d, yyyy')}
                            {isOverdue && ' (Overdue)'}
                          </>
                        ) : (
                          'No deadline set'
                        )}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setDeadlineModalOpen(true)}>
                      <Edit className="w-3 h-3 mr-1" />
                      {caseData.deadline ? 'Change' : 'Set'}
                    </Button>
                  </div>

                  <Separator />

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Created</Label>
                      <p className="text-sm text-gray-900">
                        {format(new Date(caseData.createdAt), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Last Updated</Label>
                      <p className="text-sm text-gray-900">
                        {format(new Date(caseData.updatedAt), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Task Summary */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Task Summary</CardTitle>
                    <Button size="sm" onClick={() => setCreateTaskModalOpen(true)}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Task
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {caseData.taskStats && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-gray-900">{caseData.taskStats.pending}</p>
                          <p className="text-xs text-gray-500">Pending</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-600">{caseData.taskStats.inProgress}</p>
                          <p className="text-xs text-gray-500">In Progress</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-purple-600">{caseData.taskStats.submitted + caseData.taskStats.underReview}</p>
                          <p className="text-xs text-gray-500">Awaiting Review</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-green-600">{caseData.taskStats.completed}</p>
                          <p className="text-xs text-gray-500">Completed</p>
                        </div>
                      </div>

                      {caseData.taskStats.overdue > 0 && (
                        <div className="bg-red-50 rounded-lg p-3 flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-500" />
                          <div>
                            <p className="font-medium text-red-700">{caseData.taskStats.overdue} Overdue</p>
                            <p className="text-xs text-red-600">Requires attention</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setActiveTab('tasks')}
                  >
                    View All Tasks
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Pre-Application Form Data */}
            {caseData.formData && (
              <Card className="mt-6">
                <CardContent className="pt-6">
                  <FormDataViewer
                    serviceType={caseData.serviceType}
                    formData={caseData.formData}
                  />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Student Profile Tab */}
          <TabsContent value="student" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Student Profile
                </CardTitle>
                <CardDescription>Student personal and academic information</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Info */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Personal Information</h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500">Full Name</Label>
                        <p className="text-gray-900">{caseData.student?.firstName} {caseData.student?.lastName}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Email</Label>
                        <p className="text-gray-900">{caseData.student?.email}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Phone</Label>
                        <p className="text-gray-900">{caseData.student?.phone || 'Not provided'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Country</Label>
                        <p className="text-gray-900">{caseData.student?.country || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Academic Info */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Academic Information</h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500">Education Level</Label>
                        <p className="text-gray-900 capitalize">{caseData.student?.currentEducationLevel || 'Not provided'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Field of Study</Label>
                        <p className="text-gray-900">{caseData.student?.fieldOfStudy || 'Not provided'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">GPA</Label>
                        <p className="text-gray-900">{caseData.student?.gpa || 'Not provided'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">Institution</Label>
                        <p className="text-gray-900">{caseData.student?.institution || 'Not provided'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Test Scores */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Test Scores</h4>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-gray-500">IELTS</Label>
                        <p className="text-gray-900">{caseData.student?.ielts || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">TOEFL</Label>
                        <p className="text-gray-900">{caseData.student?.toefl || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-gray-500">GRE</Label>
                        <p className="text-gray-900">{caseData.student?.gre || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Preferences */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">Preferences</h4>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-500">Preferred Countries</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {caseData.student?.preferredCountries?.length > 0 ? (
                            caseData.student.preferredCountries.map((country, idx) => (
                              <Badge key={idx} variant="outline">{country}</Badge>
                            ))
                          ) : (
                            <p className="text-gray-500">Not specified</p>
                          )}
                        </div>
                      </div>
                      <div>
                        <Label className="text-gray-500">Budget</Label>
                        <p className="text-gray-900">{caseData.student?.budget || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Documents
                </CardTitle>
                <CardDescription>Uploaded and required documents for this case</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Student Documents */}
                <h4 className="font-medium text-gray-900 mb-4">Student Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {['transcripts', 'testScores', 'sop', 'recommendation', 'resume', 'passport'].map((docType) => {
                    const docUrl = caseData.student?.documents?.[docType];
                    return (
                      <div
                        key={docType}
                        className={cn(
                          "border rounded-lg p-4",
                          docUrl ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className={cn("w-5 h-5", docUrl ? "text-green-600" : "text-gray-400")} />
                            <span className="font-medium capitalize">{docType.replace(/([A-Z])/g, ' $1').trim()}</span>
                          </div>
                          {docUrl ? (
                            <Badge className="bg-green-100 text-green-700">Uploaded</Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-500">Missing</Badge>
                          )}
                        </div>
                        {docUrl && (
                          <Button variant="link" size="sm" className="mt-2 p-0 h-auto" asChild>
                            <a href={docUrl} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              View Document
                            </a>
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Case Documents */}
                {caseData.documents?.length > 0 && (
                  <>
                    <Separator className="my-6" />
                    <h4 className="font-medium text-gray-900 mb-4">Case Attachments</h4>
                    <div className="space-y-2">
                      {caseData.documents.map((doc, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-gray-500" />
                            <div>
                              <p className="font-medium">{doc.name}</p>
                              <p className="text-xs text-gray-500">
                                Uploaded {format(new Date(doc.uploadedAt), 'MMM d, yyyy')}
                              </p>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" asChild>
                            <a href={uploadAPI.getDownloadUrl(doc.url, doc.name)} target="_blank" rel="noopener noreferrer" download={doc.name || true}>
                              <Download className="w-4 h-4" />
                            </a>
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Tasks
                    </CardTitle>
                    <CardDescription>Manage tasks linked to this case</CardDescription>
                  </div>
                  <Button onClick={() => setCreateTaskModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Task
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!caseData.tasks || caseData.tasks.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Tasks Yet</h3>
                    <p className="text-gray-500 mb-4">Create tasks to track work for this case</p>
                    <Button onClick={() => setCreateTaskModalOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Task
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {caseData.tasks.map((task) => {
                      const taskTypeConfig = TASK_TYPE_CONFIG[task.taskType] || TASK_TYPE_CONFIG.OTHER;
                      const TaskIcon = taskTypeConfig.icon;
                      const taskStatusConfig = TASK_STATUS_CONFIG[task.status] || {};
                      const isTaskOverdue = task.dueDate && isPast(new Date(task.dueDate)) && task.status !== 'COMPLETED';
                      const submission = task.submission;
                      const hasSubmission =
                        submission &&
                        (submission.submittedAt || submission.text || (submission.files && submission.files.length > 0));

                      return (
                        <div
                          key={task.taskId}
                          className={cn(
                            "border rounded-lg p-4",
                            isTaskOverdue && "border-red-200 bg-red-50/30"
                          )}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mt-0.5">
                                <TaskIcon className="w-5 h-5 text-gray-600" />
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">{task.title}</h4>
                                <p className="text-sm text-gray-500 mt-0.5">{task.description}</p>
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <Badge className={taskStatusConfig.color}>{taskStatusConfig.label}</Badge>
                                  <Badge variant="outline">{taskTypeConfig.label}</Badge>
                                  {task.assignedToRole === 'rep3' && (
                                    <Badge className="bg-purple-100 text-purple-700 text-xs">
                                      Assigned to Partner
                                    </Badge>
                                  )}
                                  {task.dueDate && (
                                    <span className={cn(
                                      "text-xs",
                                      isTaskOverdue ? "text-red-600" : "text-gray-500"
                                    )}>
                                      Due {format(new Date(task.dueDate), 'MMM d, yyyy')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {task.status !== 'COMPLETED' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCompleteTask(task.taskId)}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Complete
                              </Button>
                            )}
                          </div>

                          {hasSubmission && (
                            <div className="mt-3 ml-13 pl-3 border-l-2 border-emerald-200 bg-emerald-50/40 rounded-r-md p-3">
                              <div className="flex items-center gap-2 mb-2">
                                <Upload className="w-3.5 h-3.5 text-emerald-600" />
                                <span className="text-xs font-medium text-emerald-700">
                                  Submission
                                </span>
                                {submission.submittedAt && (
                                  <span className="text-[11px] text-gray-500">
                                    · {formatDistanceToNow(new Date(submission.submittedAt), { addSuffix: true })}
                                  </span>
                                )}
                              </div>
                              {submission.text && (
                                <p className="text-sm text-gray-700 mb-2 whitespace-pre-wrap">
                                  {submission.text}
                                </p>
                              )}
                              {Array.isArray(submission.files) && submission.files.length > 0 && (
                                <div className="space-y-1.5">
                                  {submission.files.map((f, idx) => (
                                    <a
                                      key={idx}
                                      href={uploadAPI.getDownloadUrl(f.url, f.name)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      download={f.name || true}
                                      className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-900 hover:underline"
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                      <span className="truncate">{f.name || 'Attachment'}</span>
                                      {typeof f.size === 'number' && (
                                        <span className="text-[11px] text-gray-400">
                                          ({Math.round(f.size / 1024)} KB)
                                        </span>
                                      )}
                                    </a>
                                  ))}
                                </div>
                              )}
                              {['SUBMITTED', 'UNDER_REVIEW', 'REVISION_REQUIRED'].includes(task.status) && (
                                <div className="mt-3 flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    onClick={() => handleCompleteTask(task.taskId)}
                                  >
                                    <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                    Approve &amp; Complete
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Chat with Student
                </CardTitle>
                <CardDescription>Communicate directly with the student</CardDescription>
              </CardHeader>
              <CardContent>
                <ServiceChat
                  serviceRequestId={caseData.serviceRequestId}
                  isStaffView={true}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notes Tab */}
          <TabsContent value="notes" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <StickyNote className="w-5 h-5" />
                      Notes
                    </CardTitle>
                    <CardDescription>Internal and external notes for this case</CardDescription>
                  </div>
                  <Button onClick={() => setNoteModalOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Note
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!caseData.notes || caseData.notes.length === 0 ? (
                  <div className="text-center py-12">
                    <StickyNote className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Notes Yet</h3>
                    <p className="text-gray-500 mb-4">Add notes to track important information</p>
                    <Button onClick={() => setNoteModalOpen(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add First Note
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {caseData.notes.map((note, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {note.isInternal && (
                              <Badge className="bg-amber-100 text-amber-700">Internal</Badge>
                            )}
                            <span className="text-xs text-gray-500">
                              {format(new Date(note.addedAt), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{note.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Activity History
                </CardTitle>
                <CardDescription>Complete audit log of all changes</CardDescription>
              </CardHeader>
              <CardContent>
                {!caseData.statusHistory || caseData.statusHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No History Yet</h3>
                    <p className="text-gray-500">Activity history will appear here as changes are made</p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
                    <div className="space-y-4">
                      {[...caseData.statusHistory].reverse().map((history, idx) => (
                        <div key={idx} className="flex items-start gap-4 relative">
                          <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center z-10">
                            <RefreshCw className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div className="flex-1 bg-gray-50 rounded-lg p-3">
                            <p className="font-medium text-gray-900">
                              {history.status.includes('PROGRESS_UPDATE') || history.status.includes('DEADLINE_UPDATE') || history.status.includes('PRIORITY_UPDATE')
                                ? history.status.split(': ')[0].replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase())
                                : `Status changed to ${STATUS_CONFIG[history.status]?.label || history.status}`}
                            </p>
                            {history.status.includes(': ') && (
                              <p className="text-sm text-gray-600">{history.status.split(': ')[1]}</p>
                            )}
                            {history.note && (
                              <p className="text-sm text-gray-600 mt-1">{history.note}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-2">
                              {format(new Date(history.changedAt), 'MMM d, yyyy h:mm a')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Status Update Modal */}
        <Dialog open={statusModalOpen} onOpenChange={setStatusModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Case Status</DialogTitle>
              <DialogDescription>Change the status of this case</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASSIGNED">Assigned</SelectItem>
                    <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                    <SelectItem value="WAITING_STUDENT">Waiting Student</SelectItem>
                    <SelectItem value="ON_HOLD">On Hold</SelectItem>
                    <SelectItem value="COMPLETED">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Note (Optional)</Label>
                <Textarea
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  placeholder="Add a note about this status change..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateStatus} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Progress Update Modal */}
        <Dialog open={progressModalOpen} onOpenChange={setProgressModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Progress</DialogTitle>
              <DialogDescription>Set the progress percentage for this case</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Progress: {newProgress}%</Label>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={newProgress}
                  onChange={(e) => setNewProgress(parseInt(e.target.value))}
                  className="w-full"
                />
                <Progress value={newProgress} className="h-3" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setProgressModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateProgress} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Progress
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Priority Update Modal */}
        <Dialog open={priorityModalOpen} onOpenChange={setPriorityModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Priority</DialogTitle>
              <DialogDescription>Set the priority level for this case</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select value={newPriority} onValueChange={setNewPriority}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPriorityModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdatePriority} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Priority
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deadline Update Modal */}
        <Dialog open={deadlineModalOpen} onOpenChange={setDeadlineModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Deadline</DialogTitle>
              <DialogDescription>Set or change the deadline for this case</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Deadline</Label>
                <Input
                  type="date"
                  value={newDeadline}
                  onChange={(e) => setNewDeadline(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setNewDeadline(''); }}>Clear Deadline</Button>
              <Button variant="outline" onClick={() => setDeadlineModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateDeadline} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Deadline
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Note Modal */}
        <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Note</DialogTitle>
              <DialogDescription>Add a note to this case</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Enter your note..."
                  rows={4}
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isInternal"
                  checked={isNoteInternal}
                  onChange={(e) => setIsNoteInternal(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="isInternal" className="text-sm font-normal cursor-pointer">
                  Internal note (only visible to staff)
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoteModalOpen(false)}>Cancel</Button>
              <Button onClick={handleAddNote} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Add Note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Task Modal */}
        <Dialog open={createTaskModalOpen} onOpenChange={setCreateTaskModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Task</DialogTitle>
              <DialogDescription>Create a new task for the student</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Task Type *</Label>
                <Select
                  value={newTask.taskType}
                  onValueChange={(value) => setNewTask({ ...newTask, taskType: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select task type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TASK_TYPE_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Enter task title"
                />
              </div>

              <div className="space-y-2">
                <Label>Description *</Label>
                <Textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Describe what the student needs to do"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Instructions (Optional)</Label>
                <Textarea
                  value={newTask.instructions}
                  onChange={(e) => setNewTask({ ...newTask, instructions: e.target.value })}
                  placeholder="Detailed instructions for the student"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={newTask.dueDate}
                    onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={newTask.priority}
                    onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="LOW">Low</SelectItem>
                      <SelectItem value="MEDIUM">Medium</SelectItem>
                      <SelectItem value="HIGH">High</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateTaskModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTask} disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AgentLayout>
  );
}
